import React, { useState, useEffect } from 'react'
import { T, Avatar, playColor, tdColor, GameLogos, inGameFilter } from '../ui'
import { getNFLLocks, getNFLTdLab, getNFLTopPlays, getNFLFades, getNFLGame, getNFLTdRoles } from '../api'
import { track } from '../track'
import { playText, teamNick } from '../plain'

// The week at a glance — four headline tiles a first-time user can read in five seconds, each
// with a one-tap drill-down. Founder brief: "four metrics you'd show an executive; macro info
// out front, the ability to drill down".
//
// Action = the sportsbook over/under (the market's expected points), with our projected TD
// scorers alongside. Games with no posted total are left out rather than ranked on a guess.
// Finished games are excluded everywhere — this is about what's still to play.
//
// Pick a game in the top ticker (gameFilter) and the same four tiles re-cut to that game:
// likeliest overs, likeliest unders, top TD spots, and the game line — so one tap on
// "Patriots @ Bears" answers "what's most likely to happen in this game" without a new screen.

const short = n => { const p = (n || '').split(' '); return p.length > 1 ? `${p[0][0]}. ${p.slice(1).join(' ')}` : (n || '') }

function Tile({ id, icon, title, sub, onOpen, children }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 13, padding: '10px 10px 8px', minWidth: 0,
      display: 'flex', flexDirection: 'column' }}>
      <button onClick={() => { track('tile', id); onOpen && onOpen() }} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none',
        padding: 0, cursor: 'pointer', color: T.text, textAlign: 'left', width: '100%' }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 900, lineHeight: 1.15 }}>{title}</span>
        <span style={{ color: T.green, fontWeight: 900, fontSize: 14 }}>›</span>
      </button>
      <div style={{ fontSize: 10, color: T.faint, margin: '2px 0 6px', lineHeight: 1.3 }}>{sub}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>{children}</div>
    </div>
  )
}

function Row({ left, right, rightColor, onClick, avatar }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: T.card2,
      border: `1px solid ${T.border}`, borderRadius: 8, padding: '5px 6px', cursor: 'pointer', color: T.text, textAlign: 'left', minWidth: 0 }}>
      {avatar}
      <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 700, lineHeight: 1.2 }}>{left}</span>
      <span style={{ flexShrink: 0, fontFamily: T.mono, fontSize: 12, fontWeight: 900, color: rightColor || T.text, textAlign: 'right' }}>{right}</span>
    </button>
  )
}
const ell = { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const empty = t => <div style={{ fontSize: 11, color: T.muted, padding: '6px 2px' }}>{t}</div>

export default function WeekDashboard({ season, week, games, onPlayer, onGame, setView, onOpenLocks, gameFilter }) {
  const [locks, setLocks] = useState(null)
  const [tds, setTds] = useState(null)
  useEffect(() => { let a = true; setLocks(null); setTds(null)
    getNFLLocks(season, week).then(r => { if (a) setLocks(r?.locks || []) }).catch(() => { if (a) setLocks([]) })
    getNFLTdLab(season, week).then(r => { if (a) setTds(r?.rows || []) }).catch(() => { if (a) setTds([]) })
    return () => { a = false } }, [season, week])
  // Per-game cut: loaded only once someone picks a game, then reused for every game.
  const [byGame, setByGame] = useState(null)
  const [fades, setFades] = useState(null)
  useEffect(() => { setByGame(null); setFades(null) }, [season, week])
  // Two independent loads: a shared effect keyed on byGame cancelled itself when top plays landed
  // first, dropping the fades response and leaving the unders tile on "Loading…" forever.
  const want = !!gameFilter
  useEffect(() => { if (!want) return; let a = true
    getNFLTopPlays(season, week, 8).then(r => { if (a) setByGame(r?.games || []) }).catch(() => { if (a) setByGame([]) })
    return () => { a = false } }, [want, season, week])
  useEffect(() => { if (!want) return; let a = true
    getNFLFades(season, week, 200).then(r => { if (a) setFades(r?.fades || []) }).catch(() => { if (a) setFades([]) })
    return () => { a = false } }, [want, season, week])
  // The game's matchup read (attack lanes = where each offense meets a soft defense) for the
  // "Why this game" strip — the Defense board's best angle, surfaced without digging.
  const [gread, setGread] = useState(null)
  useEffect(() => { setGread(null); if (!gameFilter) return; let a = true
    getNFLGame(gameFilter.game_id, season, week).then(r => { if (a) setGread(r || {}) }).catch(() => { if (a) setGread({}) })
    return () => { a = false } }, [gameFilter, season, week])
  // TD-by-role angles (e.g. "allows the most TDs to No. 2 WRs") — the fallback "why" when neither
  // offense has a soft yardage lane. Slate-wide, loaded once.
  const [roles, setRoles] = useState(null)
  useEffect(() => { if (!want || roles) return; let a = true
    getNFLTdRoles(season, week).then(r => { if (a) setRoles(r?.players || []) }).catch(() => { if (a) setRoles([]) })
    return () => { a = false } }, [want, season, week, roles])
  useEffect(() => { setRoles(null) }, [season, week])

  const toPlay = (games || []).filter(g => g.state !== 'final')
  const doneTeams = new Set((games || []).filter(g => g.state === 'final').flatMap(g => [g.away?.abbr, g.home?.abbr]))
  const priced = toPlay.filter(g => g.total != null)
  const byTotal = [...priced].sort((a, b) => (b.total - a.total) || ((b.proj_tds || 0) - (a.proj_tds || 0)))
  const most = byTotal.slice(0, 3)
  const least = byTotal.slice(-3).reverse().filter(g => !most.includes(g) || byTotal.length < 4)
  // Pay Dirt already ranks the TD chalk, so this tile skips anytime TDs — otherwise both tiles
  // headline the same three backs (Gibbs / Henry / McCaffrey) and the front page says it twice.
  const live = (locks || []).filter(r => !doneTeams.has(r.team))
  const nonTd = live.filter(r => r.prop_type !== 'anytime_td')
  const topPlays = (nonTd.length ? nonTd : live).slice(0, 3)   // thin late-week board: fall back to all
  const topTds = (tds || []).filter(r => !doneTeams.has(r.team)).sort((a, b) => (b.prob || 0) - (a.prob || 0)).slice(0, 3)

  const open = (r, extra) => onPlayer && onPlayer({ full_name: r.name, gsis_id: r.gsis_id, pos: r.pos, team: r.team, opp: r.opp,
    headshot: r.headshot, status: 'active', ...extra })
  const gameRow = g => (
    <Row key={g.game_id} onClick={() => onGame && onGame(g)}
      left={<><span style={{ display: 'block' }}><GameLogos away={g.away?.abbr} home={g.home?.abbr} size={18} /></span>
        {g.proj_tds != null && <span style={{ ...ell, fontSize: 10, color: T.muted, fontWeight: 600 }}>~{Math.round(g.proj_tds)} TDs projected</span>}</>}
      right={<><span style={{ display: 'block' }}>{g.total}</span><span style={{ display: 'block', fontSize: 10, color: T.faint, fontWeight: 700 }}>O/U</span></>} />
  )

  if (gameFilter) {
    const [a, h] = gameFilter.teams
    const gm = (byGame || []).find(x => x.game_id === gameFilter.game_id)
    const game = (games || []).find(x => x.game_id === gameFilter.game_id) || { game_id: gameFilter.game_id, away: { abbr: a }, home: { abbr: h } }
    const overs = gm ? [...gm.plays].sort((x, y) => (y.hit_prob || 0) - (x.hit_prob || 0)).slice(0, 3) : []
    const unders = (fades || []).filter(r => r.game_id === gameFilter.game_id || inGameFilter(gameFilter, r.team))
      .sort((x, y) => (y.prob || 0) - (x.prob || 0)).slice(0, 3)
    const gTds = (tds || []).filter(r => inGameFilter(gameFilter, r.team)).sort((x, y) => (y.prob || 0) - (x.prob || 0)).slice(0, 3)
    const total = gm?.total ?? game.total
    const sp = gm?.spread ?? game.spread                    // nflverse: > 0 = HOME favored
    const fav = sp == null || sp === 0 ? null : sp > 0 ? `${teamNick(h)} −${sp}` : `${teamNick(a)} −${-sp}`
    // Each offense's softest lane (one per team, so both sides get an angle); else the top read line.
    const UNIT_TXT = { Pass: 'the QB', Rush: 'the run game', WR: 'the WRs', TE: 'the TE' }
    const lanes = gread ? [['away', a], ['home', h]].flatMap(([side, off]) =>
      ((gread.attack || {})[side] || []).filter(l => l.verdict === 'attack')
        .sort((x, y) => (y.softness || 0) - (x.softness || 0)).slice(0, 1).map(l => ({ ...l, off }))) : []
    lanes.sort((x, y) => (y.softness || 0) - (x.softness || 0))
    const whys = lanes.slice(0, 2).map(l => {
      const who = (l.plays || [])[0]
      return { k: `${l.off}-${l.unit}`, head: `${teamNick(l.off)}: attack ${UNIT_TXT[l.unit] || l.unit}`,
        body: l.why.replace(/^Soft — /, '') + (who ? ` Top play: ${short(who.name || who.full_name || '')}.` : '') }
    })
    if (whys.length < 2) {                                   // TD-by-role PRIME spots, one per team
      const ROLE_TXT = { WR1: 'No. 1 WRs', WR2: 'No. 2 WRs', WR3: 'No. 3 WRs', TE1: 'starting TEs', TE2: 'backup TEs',
        RB1: 'lead RBs', RB2: 'backup RBs', QB: 'QB runs' }
      const seen = new Set(lanes.slice(0, 2).map(l => l.off))
      ;(roles || []).filter(r => r.tag === 'PRIME' && inGameFilter(gameFilter, r.team))
        .sort((x, y) => (x.opp_rank || 99) - (y.opp_rank || 99))
        .forEach(r => {
          if (whys.length >= 2 || seen.has(r.team)) return
          seen.add(r.team)
          const rk = r.opp_rank === 1 ? 'the most' : `the ${r.opp_rank}${['th', 'st', 'nd', 'rd'][(r.opp_rank % 10 < 4 && Math.floor(r.opp_rank / 10) !== 1) ? r.opp_rank % 10 : 0]}-most`
          whys.push({ k: `role-${r.gsis_id}`, head: `${teamNick(r.team)}: ${short(r.name)}`,
            body: `${teamNick(r.opp)} allow ${rk} TDs to ${ROLE_TXT[r.role] || r.role_label || r.role}.` })
        })
    }
    if (!whys.length && gread && (gread.read || []).length) whys.push({ k: 'read', head: 'The read', body: gread.read[0] })
    const stat = (label, val) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', background: T.card2,
        border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 8px' }}>
        <span style={{ fontSize: 10.5, color: T.muted, fontWeight: 700 }}>{label}</span>
        <span style={{ fontFamily: T.mono, fontSize: 12.5, fontWeight: 900 }}>{val ?? '—'}</span>
      </div>
    )
    return (
      <div style={{ margin: '2px 0 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '0 2px 8px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <GameLogos away={a} home={h} size={20} names />
          </span>
          <span style={{ fontSize: 10, color: T.faint, textAlign: 'right' }}>this game only · tap any tile</span>
        </div>
        {whys.length > 0 && (
          <button onClick={() => { track('tile', 'game_why'); onGame && onGame(game) }} style={{ display: 'flex', flexDirection: 'column', gap: 5,
            width: '100%', textAlign: 'left', cursor: 'pointer', color: T.text, background: 'linear-gradient(120deg,#0c2018,#0a1119)',
            border: `1px solid ${T.green}55`, borderRadius: 13, padding: '9px 12px', marginBottom: 8 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: T.green, letterSpacing: '0.08em', textTransform: 'uppercase' }}>🎯 Why this game</span>
            {whys.map(w => (
              <span key={w.k} style={{ fontSize: 12, lineHeight: 1.35 }}><b>{w.head}</b> <span style={{ color: T.muted }}>— {w.body}</span></span>
            ))}
          </button>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Tile id="game_overs" icon="📈" title="Most likely overs" sub="Highest chance to go over" onOpen={() => onGame && onGame(game)}>
            {byGame == null ? empty('Loading…') : !overs.length ? empty('Fills as lines post.') : overs.map((r, i) => (
              <Row key={i} onClick={() => open(r, { market: r.market })}
                avatar={<Avatar url={r.headshot} name={r.name} team={r.team} size={22} />}
                left={<><span style={ell}>{short(r.name)}</span>
                  <span style={{ ...ell, fontSize: 10, color: T.muted, fontWeight: 600 }}>{playText(r.market, r.line)}
                    {r.tier_note ? <span style={{ color: T.amber }}> · {r.tier_note}</span> : null}</span></>}
                right={`${Math.round((r.hit_prob || 0) * 100)}%`} rightColor={playColor(r.hit_prob || 0, r)} />
            ))}
          </Tile>
          <Tile id="game_unders" icon="📉" title="Most likely unders" sub="Where the model says the over is a trap" onOpen={() => onGame && onGame(game)}>
            {fades == null ? empty('Loading…') : !unders.length ? empty('No strong unders in this game yet.') : unders.map((r, i) => (
              <Row key={i} onClick={() => open(r, { market: r.market, prop_type: r.prop_type })}
                avatar={<Avatar url={r.headshot} name={r.name} team={r.team} size={22} />}
                left={<><span style={ell}>{short(r.name)}</span>
                  <span style={{ ...ell, fontSize: 10, color: T.muted, fontWeight: 600 }}>{playText(r.market, r.line, 'under')}</span></>}
                right={`${Math.round((r.prob || 0) * 100)}%`} rightColor={playColor(r.prob || 0, r)} />
            ))}
          </Tile>
          <Tile id="game_paydirt" icon="🏈" title="Top TD spots" sub="Most likely to score in this game" onOpen={() => setView && setView('tdlab')}>
            {tds == null ? empty('Loading…') : !gTds.length ? empty('Fills as the board loads.') : gTds.map((r, i) => (
              <Row key={i} onClick={() => open(r, { market: 'Anytime TD', prop_type: 'anytime_td' })}
                avatar={<Avatar url={r.headshot} name={r.name} team={r.team} size={22} />}
                left={<><span style={ell}>{short(r.name)}</span>
                  <span style={{ ...ell, fontSize: 10, color: T.muted, fontWeight: 600 }}>{teamNick(r.team)}</span></>}
                right={`${Math.round(r.prob || 0)}%`} rightColor={tdColor((r.prob || 0) / 100)} />
            ))}
          </Tile>
          <Tile id="game_line" icon="📊" title="The game" sub="The book's line · tap for the full breakdown" onOpen={() => onGame && onGame(game)}>
            {stat('Favorite', fav || (sp === 0 ? 'Pick’em' : null))}
            {stat('Over/under', total)}
            <button onClick={() => { track('tile', 'game_breakdown'); onGame && onGame(game) }} style={{ marginTop: 'auto', background: 'none',
              border: `1px solid ${T.green}`, borderRadius: 8, padding: '6px 8px', color: T.green, fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}>
              Full breakdown ›</button>
          </Tile>
        </div>
      </div>
    )
  }

  return (
    <div style={{ margin: '2px 0 10px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 8px' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Week {week} headlines</span>
        <span style={{ fontSize: 10, color: T.faint }}>tap a game up top for just that game</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Tile id="top_plays" icon="🎯" title="Highest-probability plays" sub="Most likely to cash, beyond TDs" onOpen={onOpenLocks}>
          {locks == null ? empty('Loading…') : !topPlays.length ? empty('Fills as lines post.') : topPlays.map((r, i) => (
            <Row key={i} onClick={() => open(r, { market: r.market, prop_type: r.prop_type })}
              avatar={<Avatar url={r.headshot} name={r.name} team={r.team} size={22} />}
              left={<><span style={ell}>{short(r.name)}</span>
                <span style={{ ...ell, fontSize: 10, color: T.muted, fontWeight: 600 }}>{r.prop_type === 'anytime_td' ? 'Anytime TD' : `${playText(r.market, r.line)}`}</span></>}
              right={`${Math.round((r.prob || 0) * 100)}%`} rightColor={playColor(r.prob || 0, r)} />
          ))}
        </Tile>
        <Tile id="paydirt" icon="🏈" title="Pay Dirt · top TD spots" sub="Most likely to score" onOpen={() => setView && setView('tdlab')}>
          {tds == null ? empty('Loading…') : !topTds.length ? empty('Fills as the board loads.') : topTds.map((r, i) => (
            <Row key={i} onClick={() => open(r, { market: 'Anytime TD', prop_type: 'anytime_td' })}
              avatar={<Avatar url={r.headshot} name={r.name} team={r.team} size={22} />}
              left={<><span style={ell}>{short(r.name)}</span>
                <span style={{ ...ell, fontSize: 10, color: T.muted, fontWeight: 600 }}>vs {teamNick(r.opp)}</span></>}
              right={`${Math.round(r.prob || 0)}%`} rightColor={tdColor((r.prob || 0) / 100)} />
          ))}
        </Tile>
        <Tile id="most_action" icon="🔥" title="Most action" sub="Highest over/unders — most points expected" onOpen={() => setView && setView('games')}>
          {!games ? empty('Loading…') : !most.length ? empty('Totals not posted yet.') : most.map(gameRow)}
        </Tile>
        <Tile id="least_action" icon="🧊" title="Least action" sub="Lowest over/unders — fewest points expected" onOpen={() => setView && setView('games')}>
          {!games ? empty('Loading…') : !least.length ? empty('Totals not posted yet.') : least.map(gameRow)}
        </Tile>
      </div>
    </div>
  )
}
